import { Grid, message, Popover, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDayCell } from './calendar-day-cell';
import { NavButton } from './nav-button';
import { WeekDays } from './week-days';
import { localize } from '/react4xp/mixins/helpers';
import { getTaxCalendarMonth } from '/react4xp/parts/calendar/actions';

dayjs.extend(isoWeek);
dayjs.extend(weekday);

const { useBreakpoint } = Grid;

type Props = {
    selectedDay: Dayjs;
    selectDay: (day: Dayjs) => void;
    translations: Record<string, string>;
    lang?: string;
};

export function Calendar({ selectedDay, selectDay, translations, lang }: Props) {
    const [cursor, setCursor] = useState<Dayjs>(selectedDay.startOf('day'));
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const [pickerYear, setPickerYear] = useState<number>(() => cursor.year());
    const [pickerMonth, setPickerMonth] = useState<number>(() => cursor.month());
    const { md } = useBreakpoint();

    const [monthEventsLoading, setMonthEventsLoading] = useState(false);
    const [eventDatesSet, setEventDatesSet] = useState<Set<string>>(new Set());

    const locale = useMemo<'RU' | 'KK' | 'QQ' | 'EN'>(() => {
        const l = String(lang || 'ru').toUpperCase();
        if (l === 'KK' || l === 'QQ' || l === 'EN' || l === 'RU') return l as any;
        return 'RU';
    }, [lang]);

    useEffect(() => {
        setCursor(selectedDay.startOf('day'));
    }, [selectedDay]);

    useEffect(() => {
        setPickerYear(cursor.year());
        setPickerMonth(cursor.month());
    }, [cursor]);

    useEffect(() => {
        let cancelled = false;

        const loadMonth = async () => {
            setMonthEventsLoading(true);
            try {
                const res = await getTaxCalendarMonth({
                    year: cursor.year(),
                    month: cursor.month() + 1,
                    locale,
                });

                const arr = Array.isArray(res.data) ? res.data : [];
                const s = new Set<string>();

                for (const e of arr) {
                    const d = dayjs(e.eventDate);
                    if (d.isValid()) s.add(d.format('YYYY-MM-DD'));
                }

                if (!cancelled) setEventDatesSet(s);
            } catch (e) {
                console.error(localize('calendar.load-month-error', translations), e);

                if (!cancelled) {
                    setEventDatesSet(new Set());
                }
            } finally {
                if (!cancelled) setMonthEventsLoading(false);
            }
        };

        loadMonth();
        return () => {
            cancelled = true;
        };
    }, [cursor, locale]);

    const clampDayToMonth = (base: Dayjs, target: Dayjs) => {
        const desiredDate = base.date();
        const last = target.endOf('month').date();
        return target.date(Math.min(desiredDate, last));
    };

    const goPrevMonth = () => {
        setCursor((c) => c.subtract(1, 'month'));
    };

    const goNextMonth = () => {
        setCursor((c) => c.add(1, 'month'));
    };

    const goPrevYear = () => {
        setCursor((c) => c.subtract(1, 'year'));
    };

    const goNextYear = () => {
        setCursor((c) => c.add(1, 'year'));
    };

    const applyMonthYear = () => {
        const next = cursor.year(pickerYear).month(pickerMonth).startOf('day');
        setCursor(next);
        setIsPickerOpen(false);

        const nextSelected = clampDayToMonth(selectedDay, next);
        selectDay(nextSelected);
    };

    const monthCalendarDays = useMemo(() => {
        const firstDay = cursor.startOf('month');
        const start = firstDay.startOf('week');

        const lastDay = cursor.endOf('month');
        const end = lastDay.endOf('week');

        const total = end.diff(start, 'day') + 1;
        return Array.from({ length: total }, (_, i) => start.add(i, 'day'));
    }, [cursor]);

    const weekCalendarDays = useMemo(() => {
        const start = cursor.weekday(0);
        return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
    }, [cursor]);

    const goPrevWeek = () => setCursor((c) => c.subtract(1, 'week'));
    const goNextWeek = () => setCursor((c) => c.add(1, 'week'));

    const hasEvents = (day: Dayjs) => eventDatesSet.has(day.format('YYYY-MM-DD'));

    return (
        <div
            className="calendar"
            style={{
                backgroundImage: "url('/_/asset/isnareactcms/images/bg_calendar.svg')",
            }}
        >
            <div className="title">
                {localize('calendar.title', translations)}
                {monthEventsLoading ? (
                    <span style={{ marginLeft: 10, opacity: 0.7 }}>…</span>
                ) : null}
            </div>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 10px',
                    marginTop: 8,
                    gap: 8,
                }}
            >
                <div style={{ display: 'flex', gap: 6 }}>
                    <NavButton
                        variant="transparent"
                        direction="left"
                        onClick={goPrevYear}
                    />
                    <NavButton
                        variant="transparent"
                        direction="left"
                        onClick={goPrevMonth}
                    />
                </div>

                <Popover
                    open={isPickerOpen}
                    onOpenChange={setIsPickerOpen}
                    trigger="click"
                    content={
                        <div
                            style={{
                                width: 220,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 10,
                            }}
                        >
                            <Select
                                value={pickerMonth}
                                onChange={setPickerMonth}
                                style={{ width: '100%' }}
                                options={[
                                    {
                                        value: 0,
                                        label: dayjs()
                                            .month(0)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 1,
                                        label: dayjs()
                                            .month(1)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 2,
                                        label: dayjs()
                                            .month(2)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 3,
                                        label: dayjs()
                                            .month(3)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 4,
                                        label: dayjs()
                                            .month(4)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 5,
                                        label: dayjs()
                                            .month(5)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 6,
                                        label: dayjs()
                                            .month(6)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 7,
                                        label: dayjs()
                                            .month(7)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 8,
                                        label: dayjs()
                                            .month(8)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 9,
                                        label: dayjs()
                                            .month(9)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 10,
                                        label: dayjs()
                                            .month(10)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                    {
                                        value: 11,
                                        label: dayjs()
                                            .month(11)
                                            .locale(dayjs.locale())
                                            .format('MMMM'),
                                    },
                                ]}
                            />

                            <Select
                                value={pickerYear}
                                onChange={setPickerYear}
                                style={{ width: '100%' }}
                                options={Array.from({ length: 11 }, (_, i) => {
                                    const y = dayjs().year() - 5 + i;
                                    return { value: y, label: String(y) };
                                })}
                            />

                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    className="nav-btn white"
                                    onClick={() => setIsPickerOpen(false)}
                                    style={{ width: 90 }}
                                >
                                    {localize('cancel', translations)}
                                </button>

                                <button
                                    type="button"
                                    className="nav-btn white"
                                    onClick={applyMonthYear}
                                    style={{ width: 90 }}
                                >
                                    {localize('button.ok', translations)}
                                </button>
                            </div>
                        </div>
                    }
                >
                    <button
                        type="button"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'white',
                            fontWeight: 800,
                            letterSpacing: 0.2,
                        }}
                    >
                        {cursor.locale(dayjs.locale()).format('MMMM YYYY')}
                    </button>
                </Popover>

                <div style={{ display: 'flex', gap: 6 }}>
                    <NavButton
                        variant="transparent"
                        direction="right"
                        onClick={goNextMonth}
                    />
                    <NavButton
                        variant="transparent"
                        direction="right"
                        onClick={goNextYear}
                    />
                </div>
            </div>

            {!md && (
                <div className="calendar_mobile__wrapper">
                    <NavButton
                        direction="left"
                        onClick={goPrevWeek}
                    />

                    <div className="calendar_mobile">
                        <WeekDays />
                        <div className="calendar_mobile__days">
                            {weekCalendarDays.map((day) => (
                                <CalendarDayCell
                                    key={day.format('YYYY-MM-DD')}
                                    isSelected={day.isSame(selectedDay, 'day')}
                                    hasEvents={hasEvents(day)}
                                    day={day}
                                    onClick={() => selectDay(day)}
                                />
                            ))}
                        </div>
                    </div>

                    <NavButton
                        direction="right"
                        onClick={goNextWeek}
                    />
                </div>
            )}

            {md && (
                <div className="calendar_desktop__wrapper">
                    <div className="calendar_desktop">
                        <WeekDays />
                        <div className="calendar_desktop__days">
                            {monthCalendarDays.map((day) => (
                                <CalendarDayCell
                                    key={day.format('YYYY-MM-DD')}
                                    isSelected={day.isSame(selectedDay, 'day')}
                                    hasEvents={eventDatesSet.has(day.format('YYYY-MM-DD'))}
                                    day={day}
                                    onClick={() => selectDay(day)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}